/**
 * Shared transcript-mapping helpers.
 *
 * Turning persisted ``Message`` rows into renderable tool cards is the one
 * piece of non-trivial glue between the DB shape and the tool-call UI, and it
 * is needed in two places: the main virtualised ``MessageList`` and the
 * sub-agent drill-in modal (which renders a sub-agent's own stream in exactly
 * the same components). Extracted here so the two can never drift — a bug fixed
 * for the main transcript is fixed for the modal too.
 *
 * Both functions are pure; the React memoisation stays at the call sites.
 */

import type { Message, ToolCallState } from "@/api/types";
import { parseUTC } from "@/api/types";

/** A tool_result row reduced to what a tool card needs. */
export interface ToolResultEntry {
  content: string;
  is_error: boolean;
  created_at: string;
}

/**
 * Index ``tool_result`` rows by ``tool_use_id`` for historical (reloaded) views.
 *
 * Defensive: a past backend bug wrote a synthetic "Success.tool_use_id: <id>"
 * placeholder when the SDK replayed an empty ToolResultBlock. Those rows still
 * exist in some user DBs. A placeholder never overwrites an entry that already
 * has real content, so if both exist for one id the real one wins regardless
 * of iteration order.
 */
export function buildToolResultMap(messages: Message[]): Map<string, ToolResultEntry> {
  const map = new Map<string, ToolResultEntry>();
  const isPlaceholder = (content: string) => {
    const s = content.trim();
    return s.startsWith("Success.") && s.includes("tool_use_id:") && s.length < 200;
  };
  for (const msg of messages) {
    if (msg.role === "tool_result" && msg.tool_use_id) {
      try {
        const blocks = JSON.parse(msg.content_json);
        const resultBlock = blocks.find((b: { type: string }) => b.type === "tool_result");
        if (!resultBlock) continue;
        const content: string = resultBlock.content ?? "";
        const existing = map.get(msg.tool_use_id);
        if (existing && typeof content === "string" && isPlaceholder(content)) {
          // Legacy bad row — keep the earlier real entry.
          continue;
        }
        map.set(msg.tool_use_id, {
          content: content || "Completed",
          is_error: !!resultBlock.is_error,
          created_at: msg.created_at,
        });
      } catch { /* ignore */ }
    }
  }
  return map;
}

/**
 * Resolve a ``tool_use`` row into the state a tool card renders from.
 *
 * A live call (still streaming over the WS) wins over the persisted row, so an
 * in-flight tool keeps its "running" spinner; everything else is reconstructed
 * from the row plus its matching result. ``liveToolCalls`` is optional — the
 * drill-in modal has no live map and reads purely from history.
 *
 * ``unresolvedIsRunning`` covers the modal's case: it polls a sub-agent that
 * may still be working, so a tool row with no result row yet is genuinely
 * *running*. The main transcript leaves this off — there, an unresolved row is
 * historical and the long-standing "Completed" fallback is what it wants.
 */
export function resolveToolCall(
  msg: Message,
  toolResultMap: Map<string, ToolResultEntry>,
  liveToolCalls?: Map<string, ToolCallState>,
  opts?: { unresolvedIsRunning?: boolean },
): ToolCallState | null {
  if (!msg.tool_use_id) return null;

  const live = liveToolCalls?.get(msg.tool_use_id);
  if (live) return live;

  let toolInput: Record<string, unknown> = {};
  try {
    const blocks = JSON.parse(msg.content_json);
    const tuBlock = blocks.find((b: { type: string }) => b.type === "tool_use");
    if (tuBlock?.input) toolInput = tuBlock.input;
  } catch { /* ignore */ }

  const resultData = toolResultMap.get(msg.tool_use_id);
  const startedAt = parseUTC(msg.created_at).getTime();

  if (!resultData && opts?.unresolvedIsRunning) {
    return {
      tool_use_id: msg.tool_use_id,
      tool_name: msg.tool_name ?? "unknown",
      input: toolInput,
      status: "running",
      started_at: startedAt,
    };
  }

  return {
    tool_use_id: msg.tool_use_id,
    tool_name: msg.tool_name ?? "unknown",
    input: toolInput,
    status: resultData?.is_error ? "error" : "success",
    result: resultData?.content ?? "Completed",
    is_error: resultData?.is_error,
    started_at: startedAt,
    duration_ms: resultData
      ? parseUTC(resultData.created_at).getTime() - startedAt
      : undefined,
  };
}
